import React, { useState, useEffect } from "react";
import { Modal, FormInput, FormSelect, FormTextarea } from "@/components/ui";
import { CreditCard, Landmark, Banknote, Receipt, CheckCircle2 } from "lucide-react";
import { fmt } from "@/lib/formatters";

const PAYMENT_METHODS = [
  { id: "bank", label: "BANK", icon: Landmark },
  { id: "upi", label: "UPI", icon: CreditCard },
  { id: "cash", label: "CASH", icon: Banknote },
  { id: "cheque", label: "CHEQUE", icon: CheckCircle2 },
];

export const DisburseExpenseModal = ({ isOpen, onClose, expense, onSubmit }) => {
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    paymentMethod: "bank",
    transactionId: "",
    bankName: "",
    branch: "",
    chequeDate: "",
    chequeNumber: "",
    upiPhone: "",
    receiverName: "",
    receiverPhone: "",
    disbursementNotes: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && expense) {
      setForm({
        date: new Date().toISOString().split("T")[0],
        paymentMethod: expense.paymentMethod || "bank",
        transactionId: expense.transactionId || "",
        bankName: expense.bankName || "",
        branch: expense.branch || "",
        chequeDate: expense.chequeDate ? new Date(expense.chequeDate).toISOString().split("T")[0] : "",
        chequeNumber: expense.chequeNumber || "",
        upiPhone: expense.upiPhone || "",
        receiverName: expense.receiverName || expense.toWhomPaid || "",
        receiverPhone: expense.receiverPhone || "",
        disbursementNotes: expense.disbursementNotes || "",
      });
    }
  }, [isOpen, expense]);

  if (!expense) return null;

  const isProject = expense.relatedToProject === true || String(expense.relatedToProject).toLowerCase() === 'yes';
  const approvedAmt = expense.approvedAmount || expense.amount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(expense._id, {
        date: form.date,
        paymentMethod: form.paymentMethod,
        transactionId: form.transactionId,
        bankName: form.bankName,
        branch: form.branch,
        chequeDate: form.chequeDate,
        chequeNumber: form.chequeNumber,
        upiPhone: form.upiPhone,
        receiverName: form.receiverName,
        receiverPhone: form.receiverPhone,
        disbursementNotes: form.disbursementNotes,
      });
      onClose();
    } catch (err) {
      // Error handled by hook
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isProject ? "Record Project Transaction" : "Record General Transaction"}
      size="max-w-2xl"
    >
      <div className="flex flex-col h-full max-h-[80vh]">


        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <form id="disburseForm" onSubmit={handleSubmit} className="space-y-6">
            
            {/* Expense Type Badge */}
            <div className="p-4 bg-slate-50/50 rounded-xl border border-slate-100 flex items-center gap-4">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Expense Type</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider ${
                isProject ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-600'
              }`}>
                {isProject ? "PROJECT RELATED" : "GENERAL"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-5">
              <FormInput
                label="Transaction Date"
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                required
              />
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-bold text-emerald-600 uppercase tracking-wider">
                  Approved Amount
                </label>
                <div className="relative h-11 bg-emerald-50/50 border border-emerald-100 rounded-lg flex items-center px-4 justify-between">
                  <span className="text-[15px] font-black text-emerald-700">₹{fmt(Math.abs(approvedAmt))}</span>
                  <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1 bg-emerald-100 px-2 py-0.5 rounded-sm">
                    Verified <CheckCircle2 size={12} />
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[13px] font-semibold text-slate-700">Payment Mode</label>
              <div className="grid grid-cols-4 gap-3">
                {PAYMENT_METHODS.map((pm) => (
                  <button
                    key={pm.id}
                    type="button"
                    onClick={() => setForm({ 
                      ...form, 
                      paymentMethod: pm.id, 
                      transactionId: "", 
                      bankName: "", 
                      branch: "", 
                      chequeDate: "",
                      chequeNumber: "",
                      upiPhone: "",
                      receiverName: expense.toWhomPaid || "",
                      receiverPhone: ""
                    })}
                    className={`h-16 flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 transition-all ${
                      form.paymentMethod === pm.id 
                        ? 'border-brand-blue bg-blue-50/50 text-brand-blue' 
                        : 'border-slate-100 bg-white text-slate-500 hover:border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <pm.icon size={20} strokeWidth={form.paymentMethod === pm.id ? 2.5 : 2} />
                    <span className="text-[11px] font-bold tracking-wider">{pm.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {form.paymentMethod === "bank" && (
              <div className="grid grid-cols-2 gap-5">
                <div className="col-span-2">
                  <FormInput
                    label="Transaction / Ref ID"
                    type="text"
                    placeholder="e.g. TRX123..."
                    value={form.transactionId}
                    onChange={e => setForm({ ...form, transactionId: e.target.value })}
                    required
                  />
                </div>
                <FormInput
                  label="Bank Name"
                  type="text"
                  placeholder="e.g. HDFC"
                  value={form.bankName}
                  onChange={e => setForm({ ...form, bankName: e.target.value })}
                  required
                />
                <FormInput
                  label="Branch"
                  type="text"
                  placeholder="Branch Name"
                  value={form.branch}
                  onChange={e => setForm({ ...form, branch: e.target.value })}
                  required
                />
              </div>
            )}

            {form.paymentMethod === "upi" && (
              <div className="grid grid-cols-2 gap-5">
                <div className="col-span-2">
                  <FormInput
                    label="UPI UTR / Ref No."
                    type="text"
                    placeholder="e.g. TRX123..."
                    value={form.transactionId}
                    onChange={e => setForm({ ...form, transactionId: e.target.value })}
                    required
                  />
                </div>
                <FormInput
                  label="UPI Phone Number"
                  type="text"
                  placeholder="10-digit Phone"
                  value={form.upiPhone}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setForm({ ...form, upiPhone: val });
                  }}
                  maxLength={10}
                  required
                />
                <FormInput
                  label="Linked Bank"
                  type="text"
                  placeholder="Bank Name"
                  value={form.bankName}
                  onChange={e => setForm({ ...form, bankName: e.target.value })}
                  required
                />
              </div>
            )}

            {form.paymentMethod === "cash" && (
              <div className="grid grid-cols-2 gap-5">
                <FormInput
                  label="Receiver Name"
                  type="text"
                  placeholder="Receiver name"
                  value={form.receiverName}
                  onChange={e => setForm({ ...form, receiverName: e.target.value })}
                  required
                />
                <FormInput
                  label="Receiver Phone"
                  type="text"
                  placeholder="10-digit number"
                  value={form.receiverPhone}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setForm({ ...form, receiverPhone: val });
                  }}
                  maxLength={10}
                  required
                />
              </div>
            )}

            {form.paymentMethod === "cheque" && (
              <div className="grid grid-cols-2 gap-5">
                <div className="col-span-2">
                  <FormInput
                    label="Transaction / Ref ID"
                    type="text"
                    placeholder="e.g. TRX123..."
                    value={form.transactionId}
                    onChange={e => setForm({ ...form, transactionId: e.target.value })}
                    required
                  />
                </div>
                <FormInput
                  label="Cheque Number"
                  type="text"
                  placeholder="6-digit No."
                  value={form.chequeNumber}
                  onChange={e => setForm({ ...form, chequeNumber: e.target.value })}
                  required
                />
                <FormInput
                  label="Cheque Date"
                  type="date"
                  value={form.chequeDate}
                  onChange={e => setForm({ ...form, chequeDate: e.target.value })}
                  required
                />
                <div className="col-span-2">
                  <FormInput
                    label="Issuing Bank"
                    type="text"
                    placeholder="Bank name on cheque"
                    value={form.bankName}
                    onChange={e => setForm({ ...form, bankName: e.target.value })}
                    required
                  />
                </div>
              </div>
            )}

            <FormInput
              label="Reason for Expense"
              type="text"
              value={expense.reason}
              disabled
              className="bg-slate-50 text-slate-500"
            />

            <div className="grid grid-cols-2 gap-5">
              <FormInput
                label="To Whom Paid"
                type="text"
                value={expense.toWhomPaid}
                disabled
                className="bg-slate-50 text-slate-500"
              />
              <FormInput
                label="By Whom Paid"
                type="text"
                value={expense.byWhomPaid || expense.requestedBy?.name}
                disabled
                className="bg-slate-50 text-slate-500"
              />
            </div>

            {expense.notes && (
              <FormTextarea
                label="Requester's Notes"
                value={expense.notes}
                disabled
                className="bg-slate-50 text-slate-500"
                rows={2}
              />
            )}

            <FormTextarea
              label={`Internal Notes (Optional) for ${form.paymentMethod}`}
              placeholder="Add your disbursement notes here..."
              value={form.disbursementNotes}
              onChange={e => setForm({ ...form, disbursementNotes: e.target.value })}
              rows={3}
            />

          </form>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3 rounded-b-2xl">
          <button 
            type="button"
            onClick={onClose} 
            className="btn-secondary w-32"
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit"
            form="disburseForm"
            className="btn-primary w-40"
            disabled={loading}
          >
            {loading ? "Processing..." : "Disburse Amount"}
          </button>
        </div>
      </div>
    </Modal>
  );
};




